import React, { useState, useEffect } from "react";
import moment from "moment/moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { useLoginResult } from "../State/LoginResultContext";
import { useEventResult } from "../State/EventResultContext";
import { useLocation, useNavigate } from "react-router-dom";
import StepIndicator from "../Components/StepIndicator";
import { API_URL } from "../utils/constants";
import { showToast } from "../utils/toast";
import { useErrorRenderer } from "../Hooks/useErrorRenderer";
import TextField from "../Components/TextField";
import AppHeader from "../Components/AppHeader";
import { useStreaming } from "../State/StreamingContext";
import { IoIosArrowBack } from "react-icons/io";

const times = Array.from({ length: 96 }, (_, i) => {
  const hour = Math.floor(i / 4);
  const minute = (i % 4) * 15;
  const time = new Date(0, 0, 0, hour, minute);
  return time.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
});

function EventForm() {
  // Calculate date and time rounded to next full hour
  const getNextHourDateTime = () => {
    const now = new Date();
    // Round up to next full hour
    const nextHour = new Date(now);
    nextHour.setHours(nextHour.getHours() + 1);
    nextHour.setMinutes(0);
    nextHour.setSeconds(0);
    nextHour.setMilliseconds(0);

    // Format date for DatePicker
    const dateStr = moment(nextHour).format("MM-DD-YYYY HH:mm:ss");

    // Extract hour, minute, and period for time selectors
    const hour24 = nextHour.getHours();
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const minuteStr = "00";
    const periodStr = hour24 >= 12 ? "PM" : "AM";

    return {
      date: dateStr,
      hour: String(hour12),
      minute: minuteStr,
      period: periodStr,
      selected: `${hour12}:${minuteStr} ${periodStr}`,
    };
  };

  const nextHourData = getNextHourDateTime();
  const { loginResults } = useLoginResult();
  
  const { eventResults, updateeventResults } = useEventResult();
  const { notifyOrgUsers } = useStreaming();
  const navigate = useNavigate();
  const location = useLocation();


  const [selected, setSelected] = useState(nextHourData.selected);
  const [message, setMessage, isError, setIsError, resetErrorState] =
    useErrorRenderer();
  const [eventName, setEventName] = useState("");
  const [loadingName, setLoadingName] = useState(true);
  const [keywords, setKeywords] = useState(loginResults?.keywords);
  const [eventLocation, setEventLocation] = useState("");
  const [eventDate, setEventDate] = useState(nextHourData.date);
  const [errors, setErrors] = useState("");
  // Recurring option temporarily disabled:
  // const [isRecurring, setIsRecurring] = useState(false);
  // const [daysOfWeek, setDaysOfWeek] = useState([]);

  
  const isEditMode =
    location.state?.mode === "edit" && Boolean(eventResults?.id);

  // Fetch event count and pre-fill event name + location
  useEffect(() => {
    if (isEditMode) {
      return;
    }

    const fetchEventCount = async () => {
      if (!loginResults?.token || !loginResults?.id) return;

      // Pre-fill location from profile's primary venue
      setEventLocation(loginResults?.primaryVenue || "Venue");

      setLoadingName(true);
      try {
        const response = await fetch(
          API_URL + `/events/${loginResults?.activeOrganization.id}/org-events`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${loginResults.token}`,
            },
          },
        );
        const data = await response.json();
        if (response.status === 200 && Array.isArray(data)) {
          const orgName = loginResults?.activeOrganization.name || "Org";
          const nextEventNumber = data.length + 1;
          setEventName(`${orgName} Event #${nextEventNumber}`);
        }
      } catch (error) {
        // If fetch fails, fall back to org name without number
        const orgName = loginResults?.activeOrganization.name || "Org";
        setEventName(`${orgName} Event #1`);
      } finally {
        setLoadingName(false);
      }
    };
    fetchEventCount();
  }, [isEditMode, loginResults]);

  useEffect(() => {
    if (!isEditMode || !eventResults?.id) {
      return;
    }

    const eventStart = new Date(eventResults.scheduledToStartAt);
    const hour24 = eventStart.getHours();
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const minuteValue = String(eventStart.getMinutes()).padStart(2, "0");
    const roundedMinute = ["00", "15", "30", "45"].includes(minuteValue)
      ? minuteValue
      : "00";
    const periodValue = hour24 >= 12 ? "PM" : "AM";

    const keywordString = Array.isArray(eventResults.keywords)
      ? eventResults.keywords.join(" ")
      : (eventResults.keywords ?? "");

    setEventName(eventResults.name ?? "");
    setEventLocation(eventResults.location ?? "");
    setEventDate(moment(eventStart).format("MM-DD-YYYY HH:mm:ss"));
    setHour(String(hour12));
    setMinute(roundedMinute);
    setPeriod(periodValue);
    setKeywords(keywordString);
    setLoadingName(false);
  }, [eventResults, isEditMode]);

  const onChangeKeywords = (_name, value) => {
    setKeywords(value);
  };
  const validationHandler = (_name, error) => {
    setErrors(error);
  };

  const parseCustomDate = (dateStr) => {
    const [datePart, timePart, modifier] = dateStr.split(" ");

    const [month, day, year] = datePart.split("-").map(Number);
    let [hours, minutes] = timePart.split(":").map(Number);

    if (modifier === "PM" && hours !== 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;

    return new Date(year, month - 1, day, hours, minutes, 0);
  };

  const createOrUpdateEvent = async (e) => {
    e.preventDefault();
    var token = "";
    if (loginResults !== null) {
      token = loginResults.token;
    }

   const arr = keywords
  .split(/[\s,]+/)
  .map((k) => k.trim())
  .filter((k) => k.length > 0);

  if (arr.length > 50) {
    showToast(
      "We can only support 50 keywords at the moment. Please reduce your keywords",
      { type: "error" },
    );
    return;
  }

    const dateOnly = eventDate.split(" ")[0];
    const date = parseCustomDate(`${dateOnly} ${selected}`);

    const event = {
      name: eventName,
      location: eventLocation,
      scheduledToStartAt: date.toISOString(),
      // recurring: isRecurring,
      // daysOfTheWeek: daysOfWeek,
      keywords: arr.join(','),
      organization_id: loginResults.activeOrganization.id,
    };

    try {
      const eventUrl = isEditMode
        ? `${API_URL}/events/${eventResults.id}`
        : `${API_URL}/events`;
      const response = await fetch(eventUrl, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(event),
      });
      const data = await response.json();
      if (response.status === 200) {
        showToast(
          isEditMode
            ? "Event updated successfully."
            : "Event created successfully.",
          { type: "success" },
        );
        updateeventResults(data);
        if (isEditMode) {
          navigate("/my-events");
        } else {
          await notifyOrgUsers({
            eventResults: data,
            loginResults,
          });
          navigate("/eventdetails");
        }
      } else {
        setMessage(
          data?.message ||
            (isEditMode
              ? "Failed to update event."
              : "Failed to create event."),
        );
        setIsError(true);
        resetErrorState();
      }
    } catch (error) {
      setMessage(
        error?.message ||
          (isEditMode ? "Failed to update event." : "Failed to create event."),
      );
      setIsError(true);
      resetErrorState();
    }
  };

  const [hour, setHour] = useState(nextHourData.hour);
  const [minute, setMinute] = useState(nextHourData.minute);
  const [period, setPeriod] = useState(nextHourData.period);

  useEffect(() => {
    setSelected(`${hour}:${minute} ${period}`);
  }, [hour, minute, period]);

  return (
    <div className="min-h-full flex-col">
      <AppHeader
        className="sm:hidden"
        isSuperUser={loginResults?.isSuperUser}
        loginResults={loginResults}
        hideMobileMenu={isEditMode}
        mobileLeading={
          isEditMode ? (
            <button
              type="button"
              className="inline-flex items-center rounded-md p-1 text-white/90 transition-colors hover:bg-white/10 hover:text-white !bg-transparent"
              onClick={() => navigate("/my-events")}
              aria-label="Back to Manage Events"
            >
              <IoIosArrowBack className="h-7 w-7 font-bold" />
            </button>
          ) : null
        }
      />
      <div className="flex min-h-dvh flex-1 flex-col sm:justify-center px-8 py-2 sm:py-12 sm:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md flex flex-col items-center">
          {isEditMode && (
            <div className="mb-2 hidden w-full justify-start sm:flex sm:mb-4">
              <button
                type="button"
                className="inline-flex items-center rounded-md p-1 text-white/90 transition-colors hover:bg-white/10 hover:text-white !bg-transparent"
                onClick={() => navigate("/my-events")}
                aria-label="Back to Manage Events"
              >
                <IoIosArrowBack className="h-7 w-7 font-bold" />
              </button>
            </div>
          )}
          <img
            src="logo192.png"
            alt="Seymour Logo"
            className="mx-auto h-11 sm:h-20 w-auto rounded-full hidden sm:block"
          />
          {!isEditMode && <StepIndicator currentStep={1} className="sm:mt-6" />}
          <h2 className="text-center text-[40px] font-extrabold text-white atkinson-font mt-2 tracking-tight">
            {isEditMode ? "Edit Event" : "Create Event"}
          </h2>
          <p className="text-center text-white atkinson-font text-base m-0">
            {isEditMode
              ? "Update the fields to modify the event"
              : "Complete the fields to set up the event"}
          </p>
        </div>

        <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
          <form
            onSubmit={createOrUpdateEvent}
            className="space-y-4 sm:space-y-3 m-0"
          >
            <div>
              <label
                htmlFor="Name"
                className="block text-xl font-bold text-white text-left atkinson-font mb-1 tracking-tight"
              >
                Event Title
              </label>
              <div className="relative">
                <input
                  id="Name"
                  name="Name"
                  type="text"
                  required
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder="e.g., Tech Summit 2026"
                  className={`block w-full rounded-md border border-[#FFFFFF] py-2 px-3 bg-[#595959] text-[#FFFFFF] text-sm placeholder:text-[#FFFFFFCC] placeholder:text-gray-400 focus:border-[#FFFFFF] focus:ring-0 ${loadingName ? "opacity-0" : ""}`}
                />
                {loadingName && (
                  <div className="absolute inset-0 rounded-md overflow-hidden border border-[#FFFFFF]">
                    <div
                      className="h-full w-full"
                      style={{
                        background:
                          "linear-gradient(90deg, #595959 25%, #6b6b6b 50%, #595959 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.5s infinite",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="Location"
                className="block text-xl font-bold text-white text-left atkinson-font mb-1 tracking-tight"
              >
                Event Location
              </label>
              <input
                id="Location"
                name="Location"
                type="text"
                required
                value={eventLocation}
                onChange={(e) => setEventLocation(e.target.value)}
                placeholder="e.g., Main Conference Hall"
                className="block w-full rounded-md border border-[#FFFFFF] py-2 px-3 bg-[#595959] text-[#FFFFFF] text-sm placeholder:text-[#FFFFFFCC] placeholder:text-gray-400 focus:border-[#FFFFFF] focus:ring-0"
              />
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-2 gap-4">
              <div className="col-span-1 sm:col-span-1">
                <label className="block text-xl font-bold text-white text-left atkinson-font mb-1 tracking-tight">
                  Date
                </label>
                <DatePicker
                  selected={eventDate}
                  minDate={new Date()}
                  onSelect={(date) => {
                    const _date = moment(date).format("MM-DD-YYYY HH:mm:ss");
                    setEventDate(_date);
                  }}
                  dateFormat="MM/dd/yyyy"
                  wrapperClassName="w-full"
                  className="block w-full rounded-md border border-[#FFFFFF] py-2 px-3 bg-[#595959] text-[#FFFFFF] text-sm placeholder:text-[#FFFFFFCC] placeholder:text-gray-400 focus:border-[#FFFFFF] focus:ring-0"
                />
              </div>

              <div className="col-span-2 sm:col-span-1">
                <label className="block text-xl font-bold text-white text-left atkinson-font mb-1 tracking-tight">
                  Time
                </label>
                <div className="flex gap-2 items-center">
                  {/* Hour */}
                  <div className="relative w-full">
                    <select
                      value={hour}
                      onChange={(e) => setHour(e.target.value)}
                      className="block w-full appearance-none rounded-md border border-[#FFFFFF] py-2 px-3 bg-[#595959] text-[#FFFFFF] text-sm focus:border-[#FFFFFF] focus:ring-0"
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="text-white">:</span>
                  {/* Minute */}
                  <div className="relative w-full">
                    <select
                      value={minute}
                      onChange={(e) => setMinute(e.target.value)}
                      className="block w-full appearance-none rounded-md border border-[#FFFFFF] py-2 px-3 bg-[#595959] text-[#FFFFFF] text-sm focus:border-[#FFFFFF] focus:ring-0"
                    >
                      {["00", "15", "30", "45"].map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="text-white">:</span>
                  {/* Period */}
                  <div className="relative w-full">
                    <select
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      className="block w-full appearance-none rounded-md border border-[#FFFFFF] py-2 px-3 bg-[#595959] text-[#FFFFFF] text-sm focus:border-[#FFFFFF] focus:ring-0"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xl font-bold text-white text-left atkinson-font tracking-tight">
                Custom Keywords for Stream
              </label>
              <p className="text-sm text-white m-0 mb-2 atkinson-font tracking-tight">
                Specific words you'd like the Speech-to-text to recognize.
                Useful for names, terms, or acronyms. Separated with commas or the Return key (50 word limit)
              </p>

              <TextField
                className="bg-[#595959] text-[#FFFFFF] border border-[#FFFFFF] rounded-md focus:border-[#FFFFFF] w-full p-2 placeholder:text-gray-400"
                style={{
                  backgroundColor: "#595959",
                  color: "#FFFFFF",
                  border: "1px solid #FFFFFFCC",
                }}
                onChange={onChangeKeywords}
                value={keywords}
                name={"details"}
                error={errors}
                isRequired={false}
                validationHandler={validationHandler}
                label={""}
                helperText={""}
                placeholder="Separate with commas or the Return key (50 word limit)"
                rows={"10"}
              />
            </div>

            {/*
              Recurring option temporarily disabled.

              <div className="mt-4 flex items-center">
                <input
                  type="checkbox"
                  checked={isRecurring ?? false}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <label className="ml-2 block text-sm font-medium text-white atkinson-font">
                  Recurring (optional)
                </label>
              </div>
              {isRecurring && (
                <div className="my-4">
                  <label className="block text-sm font-medium leading-6 text-white mb-2">
                    Days of week
                  </label>
                  <div className="flex flex-wrap gap-4">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                      (day) => (
                        <div key={day} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={daysOfWeek.includes(day)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setDaysOfWeek([...daysOfWeek, day]);
                              } else {
                                setDaysOfWeek(
                                  daysOfWeek.filter((d) => d !== day),
                                );
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                          />
                          <label className="ml-2 text-sm font-medium text-white">
                            {day}
                          </label>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            */}

            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3]"
                style={{ border: "2px solid #6A6680" }}
              >
                {isEditMode ? "Save Changes" : "Create Event"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EventForm;
